import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import Hero from "../components/Hero";
import DealOfTheDay from "../components/DealOfTheDay";
import Marquee from "../components/Marquee";
import Manifesto from "../components/Manifesto";
import ArticleCard from "../components/ArticleCard";
import OpinionCard from "../components/OpinionCard";
import LoungeTeaser from "../components/LoungeTeaser";
import CommunityGallery from "../components/CommunityGallery";
import NewsletterBlock from "../components/NewsletterBlock";
import Reveal from "../components/Reveal";
import { CATEGORIES } from "../lib/categories";

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [voices, setVoices] = useState([]);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/opinions", { params: { sort: "trending", limit: 3 } }).then((r) => setVoices(r.data)).catch(() => {});
  }, []);

  const featured = useMemo(() => articles.find((a) => a.featured) || articles[0], [articles]);
  const freshCount = useMemo(() => {
    const today = new Date().toDateString();
    return articles.filter((a) => new Date(a.created_at).toDateString() === today).length;
  }, [articles]);
  const filtered = useMemo(() => {
    const rest = articles.filter((a) => a !== featured);
    return tab === "all" ? rest : rest.filter((a) => a.category === tab);
  }, [articles, featured, tab]);

  return (
    <div>
      <Hero featured={featured} freshCount={freshCount} />
      <DealOfTheDay />
      <Marquee />

      <section id="latest" className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-4">The Latest Dispatches</p>
              <h2 className="font-display font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Fresh off <span className="font-serif italic normal-case text-ash">the desk</span>
              </h2>
            </div>
            <p className="text-ash text-sm max-w-xs leading-relaxed">
              Fashion to future tech, ink to ideas — filter the firehose by what moves you.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-10">
            <button data-testid="category-tab-button-all" onClick={() => setTab("all")}
              className={`whitespace-nowrap text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2 rounded-full border transition-colors duration-300 ${tab === "all" ? "bg-kinetic text-obsidian border-kinetic font-medium" : "border-white/10 text-ash hover:text-porcelain hover:border-white/25"}`}>
              All
            </button>
            {CATEGORIES.map((c) => (
              <button key={c.slug} data-testid={`category-tab-button-${c.slug}`} onClick={() => setTab(c.slug)}
                className={`whitespace-nowrap text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2 rounded-full border transition-colors duration-300 ${tab === c.slug ? "bg-kinetic text-obsidian border-kinetic font-medium" : "border-white/10 text-ash hover:text-porcelain hover:border-white/25"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        {filtered.length === 0 ? (
          <p className="text-ash font-mono text-sm py-16 text-center uppercase tracking-[0.2em]">Nothing here yet — check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} large={i % 7 === 0} />
            ))}
          </div>
        )}
      </section>

      <Manifesto />

      <section data-testid="community-voices-section" className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-crimson mb-4">Community Voices</p>
              <h2 className="font-display font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                The floor is <span className="font-serif italic normal-case text-ash">yours</span>
              </h2>
            </div>
            <Link to="/voices" data-testid="voices-view-all-link"
              className="group flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-porcelain border border-white/15 hover:border-crimson/60 px-6 py-3.5 rounded-full transition-colors duration-300 w-fit">
              All debates <ArrowUpRight size={14} className="text-crimson group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {voices.map((o, i) => (
            <OpinionCard key={o.id} opinion={o} index={i} onOpen={() => { window.location.href = "/voices"; }} />
          ))}
        </div>
      </section>

      <LoungeTeaser />

      <CommunityGallery />

      <NewsletterBlock />
    </div>
  );
}
